// The host's tab: the open games of this round (void, or record a result by hand), correcting any
// result, a bot playing for someone who can't for a while, and the league link.
import React from 'react';
import type { LeagueView, LeagueGame } from '../../leagues/api';
import { playerName } from '../../leagues/api';
import type { Act } from './LeaguePage';

export default function HostTools({ league: L, act, label, status }: {
  league: LeagueView; act: Act;
  label: (playerId: string) => string;                                  // "Rusty (bot)"
  status: (g: LeagueGame, round: number) => { text: string };          // "Tin won · Flag home…"
}) {
  const [fixGame, setFixGame] = React.useState('');
  const [botSeat, setBotSeat] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const done = L.status === 'done';
  const cur = L.rounds[L.current];
  const openNow = cur?.games.filter(g => !g.result && !g.voided) ?? [];
  const settledGames = L.rounds.slice(0, L.current + 1).flatMap((r, ri) => r.games.filter(g => g.result || g.voided).map(g => ({ g, ri })));
  const fix = settledGames.find(x => x.g.id === fixGame)?.g;
  const copy = (text: string) => { navigator.clipboard?.writeText(text).catch(() => undefined); setCopied(true); window.setTimeout(() => setCopied(false), 2000); };

  return (
    <div style={{ display: 'grid', gap: 20, maxWidth: 900 }}>
      {!done && (
        <section className="site-panel" style={{ display: 'grid', gap: 12 }}>
          <h2 className="site-display" style={{ fontSize: 26 }}>Round {L.current + 1}: games still open</h2>
          <p style={{ color: '#c9c7d3' }}>A voided game counts for neither player. {L.settings.voidAfterDays ? `Unplayed games are voided on their own ${L.settings.voidAfterDays} days after the rest of the round is done.` : 'Only you void games in this league.'} Record a result by hand if a game was played elsewhere.</p>
          {openNow.length === 0 && cur.games.every(g => !g.voided) && <p className="site-muted">None.</p>}
          {cur.games.filter(g => !g.result).map(g => {
            const live = L.live[g.id];
            return (
              <div key={g.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--panel-line)' }}>
                <span style={{ flex: '1 1 180px', fontWeight: 600 }}>{label(g.blue)} vs {label(g.red)}{live && <span style={{ color: '#ff8a7a' }}> · live now</span>}</span>
                {g.voided ? (
                  <><span className="site-muted">Voided</span><button type="button" className="site-btn site-btn-small" onClick={() => act('void', { gameId: g.id, undo: true })}>Undo</button></>
                ) : (
                  <>
                    <button type="button" className="site-btn site-btn-small" onClick={() => { if (window.confirm(live ? `${label(g.blue)} and ${label(g.red)} are playing this game right now. Void it anyway? Their game will stop counting and they'll be told.` : `Void ${label(g.blue)} vs ${label(g.red)}? It will count for neither player.`)) act('void', { gameId: g.id }); }}>Void</button>
                    <button type="button" className="site-btn site-btn-small" onClick={() => { if (window.confirm(`Record a win for ${label(g.blue)}?`)) act('result', { gameId: g.id, winner: g.blue, how: 'Recorded by the host' }); }}>{label(g.blue)} won</button>
                    <button type="button" className="site-btn site-btn-small" onClick={() => { if (window.confirm(`Record a win for ${label(g.red)}?`)) act('result', { gameId: g.id, winner: g.red, how: 'Recorded by the host' }); }}>{label(g.red)} won</button>
                    <button type="button" className="site-btn site-btn-small" onClick={() => { if (window.confirm('Record a draw?')) act('result', { gameId: g.id, winner: null, how: 'Recorded by the host' }); }}>Draw</button>
                  </>
                )}
              </div>
            );
          })}
        </section>
      )}
      <section className="site-panel" style={{ display: 'grid', gap: 10 }}>
        <h2 className="site-display" style={{ fontSize: 26 }}>Correct a result</h2>
        <p style={{ color: '#c9c7d3' }}>For a mis-click or a mistake: change any finished or voided game. Standings follow; rounds already finished stay finished.</p>
        {settledGames.length === 0 ? <p className="site-muted">No finished games yet.</p> : (
          <>
            <select aria-label="Game to correct" value={fixGame} onChange={e => setFixGame(e.target.value)} style={{ maxWidth: 520 }}>
              <option value="">Choose a game…</option>
              {settledGames.map(({ g, ri }) => <option key={g.id} value={g.id}>Round {ri + 1}: {label(g.blue)} vs {label(g.red)} ({status(g, ri).text})</option>)}
            </select>
            {fix && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[[fix.blue, `${label(fix.blue)} won`], [fix.red, `${label(fix.red)} won`], [null, 'Draw'], ['void', 'Voided']].map(([w, text]) => (
                  <button key={String(w)} type="button" className="site-btn site-btn-small" onClick={() => { if (window.confirm(`Change this game to: ${text}?`)) { act('correct', { gameId: fix.id, winner: w, how: 'Corrected by the host' }); setFixGame(''); } }}>{text}</button>
                ))}
              </div>
            )}
          </>
        )}
      </section>
      {!done && (
        <section className="site-panel" style={{ display: 'grid', gap: 10 }}>
          <h2 className="site-display" style={{ fontSize: 26 }}>A bot plays for someone</h2>
          <p style={{ color: '#c9c7d3' }}>If someone can't play for a while, a bot plays their games with their army until you give the seat back. They're told on their league page, and their results stay theirs.</p>
          {L.players.filter(p => p.away).map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ flex: '1 1 200px' }}><strong>{p.name}</strong> <span className="site-muted">· a bot plays for them</span></span>
              <button type="button" className="site-btn site-btn-small" onClick={() => act('seat-back', { playerId: p.id })}>Give the seat back</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select aria-label="Player to replace" value={botSeat} onChange={e => setBotSeat(e.target.value)} style={{ minWidth: 200 }}>
              <option value="">Choose a player…</option>
              {L.players.filter(p => !p.bot && p.id !== L.hostId).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button type="button" className="site-btn site-btn-small" disabled={!botSeat} onClick={() => { if (window.confirm(`A bot plays ${playerName(L, botSeat)}'s games until you give the seat back?`)) { act('replace', { playerId: botSeat }); setBotSeat(''); } }}>Hand to a bot</button>
          </div>
        </section>
      )}
      <section className="site-panel" style={{ display: 'grid', gap: 10 }}>
        <h2 className="site-display" style={{ fontSize: 26 }}>The league link</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input readOnly value={`${window.location.origin}/league/${L.id}`} aria-label="League link" onFocus={e => e.currentTarget.select()} style={{ flex: 1, minWidth: 0, maxWidth: 460 }} />
          <button type="button" className="site-btn" onClick={() => copy(`${window.location.origin}/league/${L.id}`)}>{copied ? 'Copied!' : 'Copy link'}</button>
        </div>
        <p className="site-muted site-small">Anyone with the link can follow the league. Someone on a new device gets their seat back with their recovery code (shown on their My army tab).</p>
      </section>
    </div>
  );
}
